import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';

/**
 * QA-552 주문 로그 — `GET /admin/v1/orders/{orderId}/logs`(BE 전달 문서, 고도몰 es_logOrder 대응).
 * 최초 주문·주문/결제/배송/클레임 상태 변경·취소·환불 등 주문에 일어난 변경을 시간순으로 받는다.
 * BE 반영 전(404·501)에는 null — 화면에 "서버 반영 후" 안내를 띄운다.
 */
export type OrderLogActorType = 'ADMIN' | 'CUSTOMER' | 'SYSTEM' | 'LEGACY';

export interface OrderLogEntry {
  occurredAt: string;
  actorType: OrderLogActorType | string;
  /** 관리자 로그인 ID·고객 아이디·"System" 등 표시용. */
  actorName: string | null;
  actorIp: string | null;
  /** 상품 줄 단위 변경이면 그 상품명(주문 단위면 null). */
  productName: string | null;
  /** 변경 전·후 상태(표시 이름). 최초 주문은 before 가 null. */
  beforeLabel: string | null;
  afterLabel: string | null;
  reason: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

export const useOrderLogsQuery = (orderId: string | undefined, enabled: boolean) =>
  useQuery<OrderLogEntry[] | null>({
    queryKey: ['admin', 'order', orderId ?? '', 'logs'],
    enabled: Boolean(orderId) && enabled,
    retry: false,
    queryFn: async () => {
      const res = await adminFetch(`/api/admin/v1/orders/${orderId}/logs`);
      if (res.status === 404 || res.status === 501) return null;
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: unknown };
      const raw = Array.isArray(env.data)
        ? env.data
        : Array.isArray((env.data as { items?: unknown } | null)?.items)
          ? ((env.data as { items: unknown[] }).items)
          : [];
      return raw.map((r) => {
        const o = (r ?? {}) as Record<string, unknown>;
        return {
          occurredAt: str(o.occurredAt) ?? '',
          actorType: str(o.actorType) ?? 'SYSTEM',
          actorName: str(o.actorName),
          actorIp: str(o.actorIp),
          productName: str(o.productName),
          beforeLabel: str(o.beforeLabel),
          afterLabel: str(o.afterLabel),
          reason: str(o.reason),
        };
      });
    },
  });
